import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "../Company/Company.css";
import { useCombinedContext } from "../../context/CombinedContext";
import "./User.css";

const UserList = () => {
  const [user, setUser] = useState([]);
  const [filterUser, setFilterUser] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // New state for pages modal
  const [selectedUserForPages, setSelectedUserForPages] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageAction, setPageAction] = useState(null); // "add" or "remove"

  const { company } = useCombinedContext();
  const companies = company || [];

  // Predefined routes (pages) options.
  // "profile" is not included in the selectable pages as it is mandatory for all users.
  const routes = [
    { value: "location", label: "Job Location" },
    { value: "vessel", label: "Vessel" },
    { value: "invoice", label: "Invoice" },
    { value: "report", label: "Report" },
    { value: "employee", label: "Employee" },
    { value: "loan", label: "Loan" },
    { value: "logistics", label: "Logistics" },
    { value: "foodallowance", label: "Food Allowance" },
    // Note: "profile" is assumed to be present on the user data by default but is excluded here.
  ];

  // Get All Users
  const getAllUser = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/allusers");
      setUser(response.data.data || []);
      setFilterUser(response.data.data || []);
    } catch (error) {
      // console.error("Error fetching users", error);
      toast.error("Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  // Add Companies to User
  const addCompaniesToUser = async () => {
    if (selectedUser && selectedCompanies.length > 0) {
      try {
        const updatedCompanies = [
          ...selectedUser.company,
          ...selectedCompanies.map((id) => ({
            company_name: companies.find((c) => c._id === id).company_name,
            _id: id,
          })),
        ];

        await axiosInstance.patch(`/auth/user/${selectedUser._id}`, {
          company: updatedCompanies,
        });

        const updatedUsers = user.map((u) =>
          u._id === selectedUser._id ? { ...u, company: updatedCompanies } : u,
        );
        setUser(updatedUsers);
        setFilterUser(updatedUsers);
        toast.success("Companies added successfully.");
        setSelectedUser(null);
        setSelectedCompanies([]);
      } catch (error) {
        toast.error("Failed to add companies.");
        // console.error(error);
      }
    } else {
      toast.warning("Please select at least one company.");
    }
  };

  // Toggle User Status
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const updatedStatus = !currentStatus; // Toggle status

      await axiosInstance.patch(`/auth/user/${userId}`, {
        status: updatedStatus,
      });

      const updatedUsers = user.map((u) =>
        u._id === userId ? { ...u, status: updatedStatus } : u,
      );
      setUser(updatedUsers);
      setFilterUser(updatedUsers);

      toast.success(
        updatedStatus
          ? "User activated successfully."
          : "User deactivated successfully.",
      );
    } catch (error) {
      toast.error("Failed to update user status.");
      // console.error(error);
    }
  };

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;

    if (checked) {
      setSelectedCompanies([...selectedCompanies, value]);
    } else {
      setSelectedCompanies(
        selectedCompanies.filter((company) => company !== value),
      );
    }
  };

  // For pages modal checkbox changes
  const handlePageCheckboxChange = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;

    if (checked) {
      setSelectedPages([...selectedPages, value]);
    } else {
      setSelectedPages(selectedPages.filter((page) => page !== value));
    }
  };

  const getSelectableCompanies = () => {
    if (!selectedUser) return companies;
    return companies.filter(
      (company) =>
        !selectedUser.company.some(
          (userCompany) => userCompany._id === company._id,
        ),
    );
  };

  // Get selectable pages for add or remove, excluding "profile"
  const getSelectablePages = () => {
    if (!selectedUserForPages) return [];
    // Filter out "profile" from the routes list.
    const filteredRoutes = routes.filter(
      (routeOption) => routeOption.value !== "profile",
    );
    if (pageAction === "add") {
      // Filter out pages that are already assigned
      return filteredRoutes.filter(
        (routeOption) =>
          !selectedUserForPages.route.includes(routeOption.value),
      );
    } else if (pageAction === "remove") {
      // Allow removal only of pages that are in the filtered list (i.e. not "profile")
      return filteredRoutes.filter((routeOption) =>
        selectedUserForPages.route.includes(routeOption.value),
      );
    }
    return [];
  };
  const confirmRoleUpdate = async () => {
    if (!roleChangeUser || !newRole) return;

    try {
      await axiosInstance.patch(`/auth/user/${roleChangeUser._id}`, {
        role: newRole,
      });

      const updatedUsers = user.map((u) =>
        u._id === roleChangeUser._id ? { ...u, role: newRole } : u,
      );

      setUser(updatedUsers);
      setFilterUser(updatedUsers);

      toast.success("User role updated successfully.");
    } catch (error) {
      toast.error("Failed to update user role.");
    } finally {
      setRoleChangeUser(null);
      setNewRole("");
    }
  };

  // Function to add or remove pages for a user
  const updatePagesToUser = async () => {
    if (selectedUserForPages && selectedPages.length > 0) {
      try {
        let updatedPages;
        if (pageAction === "add") {
          updatedPages = [...selectedUserForPages.route, ...selectedPages];
        } else if (pageAction === "remove") {
          updatedPages = selectedUserForPages.route.filter(
            (r) => !selectedPages.includes(r),
          );
        }

        await axiosInstance.patch(`/auth/user/${selectedUserForPages._id}`, {
          route: updatedPages,
        });

        const updatedUsers = user.map((u) =>
          u._id === selectedUserForPages._id
            ? { ...u, route: updatedPages }
            : u,
        );
        setUser(updatedUsers);
        setFilterUser(updatedUsers);
        toast.success(
          pageAction === "add"
            ? "Pages added successfully."
            : "Pages removed successfully.",
        );
        setSelectedUserForPages(null);
        setSelectedPages([]);
        setPageAction(null);
      } catch (error) {
        toast.error("Failed to update pages.");
        // console.error(error);
      }
    } else {
      toast.warning("Please select at least one page.");
    }
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // const itemsPerPage = 5;/
  const currentItems = [...filterUser]
    .reverse()
    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  const pageCount = Math.ceil(filterUser.length / itemsPerPage);

  useEffect(() => {
    getAllUser();
  }, []);

  const handleRoleChange = (userId, role) => {
    setRoleChangeUser(user.find((u) => u._id === userId));
    setNewRole(role);
  };

  return (
    <div className="">
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex">
          <input
            className="form-control me-3"
            type="search"
            placeholder="Search..."
            style={{ maxWidth: "200px", border: "1px solid #1E2A5E" }}
            onChange={(e) => {
              const searchText = e.target.value.toLowerCase();
              const filteredUser = user.filter(
                (u) =>
                  u.name.toLowerCase().includes(searchText) ||
                  u.email.toLowerCase().includes(searchText) ||
                  u.company.some((c) =>
                    c.company_name.toLowerCase().includes(searchText),
                  ),
              );
              setFilterUser(filteredUser);
            }}
          />
          <div className="d-flex align-items-center">
            <label className="me-2 pt-1">Count</label>
            <select
              className="form-select no-arrow"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="30">30</option>
            </select>
          </div>
        </div>
      </div>

      <div
        className="my-3 rounded"
        style={{
          overflowX: "auto",
        }}
      >
        <table className="table table-bordered">
          <thead className="t_head">
            <tr className="text-center">
              <th>S No</th>
              <th>User Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Company</th>
              <th>Pages</th>
              <th>User Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8">
                  <div className="d-flex justify-content-center m-3">
                    <div className="spinner"></div>
                  </div>
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan="8">
                  <div className="d-flex justify-content-center m-1">
                    <h6>Data Not Found</h6>
                  </div>
                </td>
              </tr>
            ) : (
              currentItems.map((data, index) => (
                <tr key={data._id}>
                  <td className="text-center">
                    {currentPage * itemsPerPage + index + 1}
                  </td>
                  <td>{data.name}</td>
                  <td>{data.email}</td>
                  {/* <td className="text-center">{data.role}</td> */}
                  <td className="text-center">
                    {data.role != "superadmin" ? (
                      <select
                        className="form-select custom-select-sm"
                        style={{ fontSize: "2px" }}
                        value={data.role}
                        onChange={(e) =>
                          handleRoleChange(data._id, e.target.value)
                        }
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        {/* Add other roles as needed */}
                      </select>
                    ) : (
                      <span>{data.role}</span>
                    )}
                  </td>

                  <td>
                    {data.company.map((company) => (
                      <div
                        key={company._id}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <p>{company.company_name}</p>
                        {/* Remove company button can be added here if needed */}
                      </div>
                    ))}
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setSelectedUser(data)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      <span>Add</span>
                    </button>
                  </td>
                  <td>
                    {/*
                      Filter out "profile" from the pages displayed in the table.
                    */}
                    {data.route
                      .filter((route) => route !== "profile")
                      .map((route, id) => (
                        <div
                          key={id}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <p>{route}</p>
                        </div>
                      ))}
                    <div className="">
                      <button
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={() => {
                          setSelectedUserForPages(data);
                          setPageAction("add");
                        }}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        <span>Add</span>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          setSelectedUserForPages(data);
                          setPageAction("remove");
                        }}
                      >
                        <i className="bi bi-trash3 me-2"></i>
                        <span>Remove</span>
                      </button>
                    </div>
                  </td>
                  <td className="text-center">
                    {data.status ? "Active" : "Inactive"}
                  </td>
                  <td className="text-center">
                    <button
                      className={`btn btn-sm ${
                        data.status ? "btn-danger" : "btn-success"
                      }`}
                      onClick={() => toggleUserStatus(data._id, data.status)}
                    >
                      {data.status ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Companies Modal */}
      {selectedUser && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          aria-labelledby="addCompaniesModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="addCompaniesModalLabel">
                  Select Companies to Add
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setSelectedUser(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="modalDiv">
                  {getSelectableCompanies().map((company) => (
                    <div key={company._id} className="form-check">
                      <input
                        type="checkbox"
                        value={company._id}
                        className="form-check-input"
                        id={`company-${company._id}`}
                        checked={selectedCompanies.includes(company._id)}
                        onChange={handleCheckboxChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`company-${company._id}`}
                      >
                        {company.company_name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="button-2"
                  data-bs-dismiss="modal"
                  onClick={() => setSelectedUser(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="button-2"
                  onClick={addCompaniesToUser}
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Companies"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pages Modal */}
      {selectedUserForPages && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          aria-labelledby="pagesModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="pagesModalLabel">
                  {pageAction === "add"
                    ? "Select Pages to Add"
                    : "Select Pages to Remove"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    setSelectedUserForPages(null);
                    setSelectedPages([]);
                    setPageAction(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="modalDiv">
                  {getSelectablePages().length > 0 ? (
                    getSelectablePages().map((pageOption) => (
                      <div key={pageOption.value} className="form-check">
                        <input
                          type="checkbox"
                          value={pageOption.value}
                          className="form-check-input"
                          id={`page-${pageOption.value}`}
                          checked={selectedPages.includes(pageOption.value)}
                          onChange={handlePageCheckboxChange}
                          style={{ cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`page-${pageOption.value}`}
                          style={{ cursor: "pointer" }}
                        >
                          {pageOption.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p>No pages available to {pageAction}.</p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="button-2"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setSelectedUserForPages(null);
                    setSelectedPages([]);
                    setPageAction(null);
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="button-2"
                  onClick={updatePagesToUser}
                  disabled={loading}
                >
                  {loading
                    ? "Updating..."
                    : pageAction === "add"
                      ? "Add Pages"
                      : "Remove Pages"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {roleChangeUser && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          aria-labelledby="confirmRoleModal"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Role Change</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setRoleChangeUser(null);
                    setNewRole("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to change role of{" "}
                <strong>{roleChangeUser.name}</strong> to{" "}
                <strong>{newRole}</strong>?
              </div>
              <div className="modal-footer">
                <button
                  className="button-2"
                  onClick={() => {
                    setRoleChangeUser(null);
                    setNewRole("");
                  }}
                >
                  Cancel
                </button>
                <button className="button-2" onClick={confirmRoleUpdate}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ReactPaginate
        pageCount={pageCount}
        onPageChange={handlePageClick}
        containerClassName="pagination justify-content-center"
        activeClassName="active"
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        nextClassName="page-item"
        previousLinkClassName="page-link"
        nextLinkClassName="page-link"
      />
    </div>
  );
};

export default UserList;
